import React, { useState, useEffect } from "react";
import T from '../../components/common/T';
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePurchaseOrder } from "../../context/PurchaseOrderContext";
import { useInventory } from "../../context/InventoryContext"; 
// import Layout from "../../components/Layout";
import OdooFormLayout from "../../components/OdooFormLayout";
import { toast } from "react-toastify";
import { PrinterIcon } from "@heroicons/react/24/outline";

// 🟢 SECURITY IMPORT
import { useAuth } from "../../context/AuthContext";

export default function ReceiptForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const poId = searchParams.get("poId");

  const { purchaseOrders, markAsDelivered } = usePurchaseOrder();
  const { warehouses } = useInventory(); 

  // 🟢 GET CHECKER
  const { checkAccess } = useAuth();

  // 🟢 PAGE GUARD
  useEffect(() => {
    if (!checkAccess('inventory.manage')) {
        toast.error("Access Denied: Only Warehouse Managers can process receipts.");
        navigate('/admin/inventory');
    }
  }, [checkAccess, navigate]);

  const [receipt, setReceipt] = useState(null);
  
  // State for Destination Selection
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [selectedBin, setSelectedBin] = useState("");

  // Load Receipt Data & Defaults
  useEffect(() => {
    const po = purchaseOrders.find(p => String(p.id) === String(poId));
    if (po) {
        setReceipt({
            name: `WH/IN/${String(po.id).padStart(5, '0')}`,
            vendor: po.vendorName,
            sourceDoc: po.poNumber,
            product: po.materialName,
            demand: po.quantity,
            done: po.status === "Received" ? po.quantity : 0,
            status: po.status === "Received" ? "DONE" : "READY"
        });
        
        // Default to first warehouse/bin if available
        if (warehouses.length > 0) {
            setSelectedWarehouseId(warehouses[0].id);
            if (warehouses[0].bins && warehouses[0].bins.length > 0) {
                setSelectedBin(warehouses[0].bins[0]);
            }
        }
    }
  }, [poId, purchaseOrders, warehouses]);

  // Handle Warehouse Change to update Bins
  useEffect(() => {
      if (selectedWarehouseId) {
          const wh = warehouses.find(w => String(w.id) === String(selectedWarehouseId));
          if (wh && wh.bins.length > 0) setSelectedBin(wh.bins[0]);
          else setSelectedBin("");
      }
  }, [selectedWarehouseId, warehouses]);

  const handleValidate = () => {
      if (!selectedWarehouseId || !selectedBin) {
          return toast.error("Please select a destination Warehouse and Bin.");
      }
      
      // 1. Mark PO as Received
      markAsDelivered(poId);

      // 2. Add Stock to Inventory (In a real app, calling `addStock` context)
      // addStock(po.materialId, po.quantity, selectedWarehouseId, selectedBin);

      toast.success(`Receipt Validated! Stock moved to ${selectedBin}`);
      navigate("/admin/inventory");
  };

  if (!checkAccess('inventory.manage')) return null;
  if (!receipt) return <><div className="p-10"><T>Loading...</T></div></>;

  return (
    <>
        <OdooFormLayout
            title={receipt.name}
            subtitle={receipt.sourceDoc}
            status={receipt.status}
            onSave={receipt.status !== "DONE" ? handleValidate : undefined}
            saveLabel="Validate"
            onCancel={() => navigate("/admin/inventory")}
        >
            <div className="grid grid-cols-2 gap-8 mb-8 text-sm">
                <div>
                    <label className="block text-gray-500 font-bold mb-1"><T>Receive From</T></label>
                    <div className="font-medium text-gray-900">{receipt.vendor}</div>
                </div>
                <div>
                    <label className="block text-gray-500 font-bold mb-1"><T>Destination Location</T></label>
                    <div className="flex gap-2">
                        <select 
                            className="border rounded p-1 bg-white outline-none focus:ring-1 focus:ring-purple-500"
                            value={selectedWarehouseId}
                            onChange={(e) => setSelectedWarehouseId(e.target.value)}
                            disabled={receipt.status === "DONE"}
                        >
                            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                        <select 
                            className="border rounded p-1 bg-white outline-none focus:ring-1 focus:ring-purple-500"
                            value={selectedBin}
                            onChange={(e) => setSelectedBin(e.target.value)}
                            disabled={receipt.status === "DONE"}
                        >
                            {warehouses.find(w => String(w.id) === String(selectedWarehouseId))?.bins.map(bin => (
                                <option key={bin} value={bin}>{bin}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <h3 className="font-bold text-purple-700 border-b-2 border-purple-700 inline-block mb-4 text-base uppercase"><T>Operations</T></h3>
            <table className="w-full text-left text-sm">
                <thead className="text-gray-500 font-bold border-b bg-gray-50">
                    <tr>
                        <th className="px-3 py-2"><T>Product</T></th>
                        <th className="px-3 py-2 text-right"><T>Demand</T></th>
                        <th className="px-3 py-2 text-right"><T>Done</T></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="px-3 py-3">{receipt.product}</td>
                        <td className="px-3 py-3 text-right">{receipt.demand}</td>
                        <td className="px-3 py-3 text-right">
                            {receipt.status === "DONE" ? (
                                <span className="text-green-700 font-bold">{receipt.done}</span>
                            ) : (
                                <input 
                                    type="number" 
                                    defaultValue={receipt.demand} 
                                    className="w-16 border rounded text-right px-1 outline-none focus:ring-1 focus:ring-purple-500" 
                                />
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </OdooFormLayout>
    </>
  );
}